import { useMemo, useState } from "react";
import type { InsightsRow } from "../../api/gigAdmin";
import { KpiCard } from "../../components/admin/AdminUi";
import { Banner } from "../../components/Banner";
import { FileButton } from "../../components/FileButton";
import {
  describeDays,
  downloadCsv,
  funnel,
  guessEmailColumn,
  guessIdColumn,
  joinMaster,
  notInMaster,
  parseCsv,
  pct,
  segmentColumns,
  sliceBy,
  slugify,
  STAGES,
  stepGaps,
  type JoinedRecord,
  type MasterList,
} from "../../lib/insights";

interface InsightsScreenProps {
  rows: InsightsRow[];
  /** False when the viewer can't see finance (the API sends financeReferred as null). */
  showFinance: boolean;
}

interface Loaded extends MasterList {
  emailCol: number;
  idCol: number;
  seg: number;
}

const selectClass =
  "h-10 w-full rounded-[10px] border-[1.5px] border-border bg-surface px-3 text-[13.5px] font-semibold";

function PctBar({ value, count }: { value: number; count?: number }) {
  const width = Math.max(0, Math.min(100, value));
  return (
    <div
      className="flex min-w-[120px] items-center gap-2"
      title={`${count != null ? `${count} people, ` : ""}${value}%`}
    >
      <div className="h-2.5 flex-1 overflow-hidden rounded-[4px] bg-surface-high">
        <i className="block h-full rounded-r-[4px] bg-accent" style={{ width: `${width}%` }} />
      </div>
      <span className="min-w-[36px] text-right font-bold tabular">{value}%</span>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-[14px] border border-border bg-surface p-5">
      <h3 className="m-0 text-[16px] font-bold">{title}</h3>
      {hint ? <p className="m-0 mt-1 text-[13px] text-text-muted">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * Who is progressing, and where they stall. The master list is read in this browser only: nothing is uploaded or
 * saved, and closing the page clears it.
 */
export function InsightsScreen({ rows, showFinance }: InsightsScreenProps) {
  const [master, setMaster] = useState<Loaded | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const read = async (file: File) => {
    setFileError(null);
    if (!/\.(csv|txt|tsv)$/i.test(file.name)) {
      setFileError(
        "Please choose a CSV file. In Excel or Google Sheets, use File, Save as (or Download), CSV.",
      );
      return;
    }
    const { head, rows: data } = parseCsv(await file.text());
    if (!head.length || !data.length) {
      setFileError("That file looks empty.");
      return;
    }
    const emailCol = guessEmailColumn(head, data);
    const idCol = guessIdColumn(head, data);
    const segs = segmentColumns({ head, rows: data }, emailCol, idCol);
    setMaster({ name: file.name, head, rows: data, emailCol, idCol, seg: segs[0] ?? -1 });
  };

  const setColumn = (key: "emailCol" | "idCol" | "seg", value: number) =>
    setMaster((m) => {
      if (!m) return m;
      const next = { ...m, [key]: value };
      if (key !== "seg") {
        const segs = segmentColumns(next, next.emailCol, next.idCol);
        if (!segs.includes(next.seg)) next.seg = segs[0] ?? -1;
      }
      return next;
    });

  // Without a master list the funnel covers everyone who has signed in to the engine.
  const joined: JoinedRecord[] = useMemo(
    () =>
      master
        ? joinMaster(master, master.emailCol, master.idCol, rows)
        : rows.map((r) => ({ row: [], engine: r })),
    [master, rows],
  );
  const segs = useMemo(
    () => (master ? segmentColumns(master, master.emailCol, master.idCol) : []),
    [master],
  );
  const stages = funnel(joined);
  const gaps = stepGaps(joined);
  const groups = useMemo(
    () => (master && master.seg >= 0 ? sliceBy(master.head, joined, master.seg, showFinance) : []),
    [master, joined, showFinance],
  );
  const matched = joined.filter((j) => j.engine).length;
  const missing = master ? notInMaster(rows, joined) : [];
  const N = joined.length;

  const columnSelect = (key: "emailCol" | "idCol" | "seg", label: string, cols: number[]) =>
    master ? (
      <label className="flex flex-col gap-1 text-[13px] font-bold">
        {label}
        <select
          className={selectClass}
          value={master[key]}
          onChange={(e) => setColumn(key, Number(e.target.value))}
        >
          <option value={-1}>(none)</option>
          {cols.map((c) => (
            <option key={c} value={c}>
              {master.head[c]}
            </option>
          ))}
        </select>
      </label>
    ) : null;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="m-0 text-[20px]">Who is progressing, and where they stall.</h2>
        <p className="m-0 mt-1 max-w-[760px] text-[14px] text-text-muted">
          Load a CSV export of your master participant list (bio data, state, gender, cohort,
          anything). It's matched to engine records by email, then participant ID, and every other
          column becomes a way to slice the results.
        </p>
      </div>
      <Banner tone="warn">
        <b>Private by design:</b> the file is read in this browser only. Nothing is uploaded or
        saved, and closing the page clears it.
      </Banner>

      <Section
        title="Master list (CSV)"
        hint="Have an Excel file? Open it, then File, Save as, CSV."
      >
        <div className="flex flex-wrap items-center gap-3">
          <FileButton
            label="Choose master list CSV"
            ariaLabel="Master list CSV"
            accept=".csv,.txt,.tsv,text/csv"
            hasFile={Boolean(master)}
            onFile={(file) => void read(file)}
          />
          {master ? (
            <button
              type="button"
              onClick={() => setMaster(null)}
              className="text-[13px] font-semibold text-accent hover:underline"
            >
              Clear the list
            </button>
          ) : null}
        </div>
        {fileError ? <Banner tone="bad">{fileError}</Banner> : null}
      </Section>

      {master ? (
        <Section
          title="Columns"
          hint={`${master.name}: ${N} records, ${master.head.length} columns. Check the matching columns, then choose what to slice by.`}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {columnSelect(
              "emailCol",
              "Email column",
              master.head.map((_, c) => c),
            )}
            {columnSelect(
              "idCol",
              "Participant ID column",
              master.head.map((_, c) => c),
            )}
            {columnSelect("seg", "Slice results by", segs)}
          </div>
        </Section>
      ) : null}

      {master ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <KpiCard label="On the master list" value={N} tone="accent" />
          <KpiCard
            label="Started the engine"
            value={matched}
            tone="positive"
            hint={`${pct(matched, N)}% of the list`}
          />
          <KpiCard
            label="Not started"
            value={N - matched}
            tone="warning"
            hint={
              <button
                type="button"
                className="font-semibold text-accent underline"
                onClick={() =>
                  downloadCsv(
                    "gateway-not-started.csv",
                    master.head,
                    joined.filter((j) => !j.engine).map((j) => j.row),
                  )
                }
              >
                Download the list
              </button>
            }
          />
        </div>
      ) : null}

      <Section
        title={master ? "The funnel, whole master list" : "The funnel, everyone in the engine"}
        hint={
          master
            ? undefined
            : `${rows.length} participants have signed in. Load a master list to see who hasn't started and to slice by it.`
        }
      >
        <table className="w-full border-collapse text-[14px]">
          <tbody>
            {stages.map((s) => (
              <tr key={s.label} className="border-b border-[#efe3d2] last:border-0">
                <td className="py-2 pr-3">{s.label}</td>
                <td className="py-2 pr-3 text-right tabular">{s.count}</td>
                <td className="w-1/2 py-2">
                  <PctBar value={s.pct} count={s.count} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {master && master.seg >= 0 ? (
        <Section
          title={`Progress by ${master.head[master.seg]}`}
          hint={`Share of each group that has reached each step. "Biggest drop" is where that group loses the most people.`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] border-collapse text-[13px]">
              <thead>
                <tr className="text-left text-[11px] font-bold uppercase tracking-[0.07em] text-text-muted">
                  <th className="px-2 py-2">{master.head[master.seg]}</th>
                  <th className="px-2 py-2">People</th>
                  {STAGES.map((s) => (
                    <th key={s.label} className="px-2 py-2">
                      {s.short}
                    </th>
                  ))}
                  <th className="px-2 py-2">Biggest drop</th>
                  <th className="px-2 py-2">Avg bid score</th>
                  {showFinance ? <th className="px-2 py-2">Finance referred</th> : null}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <tr key={g.value} className="border-t border-[#efe3d2]">
                    <td className="px-2 py-2 font-bold">{g.value}</td>
                    <td className="px-2 py-2 tabular">{g.people}</td>
                    {g.rates.map((r, i) => (
                      <td key={i} className="px-2 py-2">
                        <PctBar value={r} count={g.counts[i] ?? 0} />
                      </td>
                    ))}
                    <td className="px-2 py-2">
                      {g.drop ? (
                        <>
                          {g.drop.at}{" "}
                          <span className="text-[12px] text-text-muted">
                            (−{g.drop.points} pts)
                          </span>
                        </>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td className="px-2 py-2 tabular">{g.avgBid != null ? `${g.avgBid}%` : "–"}</td>
                    {showFinance ? (
                      <td className="px-2 py-2">
                        <PctBar value={g.financeReferred ?? 0} />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="mt-3 h-9 rounded-[9px] border border-border bg-surface px-3 text-[13px] font-semibold hover:border-accent hover:text-accent"
            onClick={() => {
              const col = master.head[master.seg] ?? "column";
              downloadCsv(
                `gateway-insights-by-${slugify(col)}.csv`,
                [col, "people", ...STAGES.map((s) => `${s.label} %`)],
                groups.map((g) => [g.value, g.people, ...g.rates]),
              );
            }}
          >
            Download this table as CSV
          </button>
        </Section>
      ) : null}

      <Section
        title="How long steps take"
        hint="Typical (median) time between finishing one step and the next."
      >
        <table className="w-full border-collapse text-[14px]">
          <tbody>
            {gaps.map((g) => (
              <tr key={g.to} className="border-b border-[#efe3d2] last:border-0">
                <td className="py-2 pr-3">
                  Step {g.from} to Step {g.to}
                </td>
                <td className="py-2 pr-3 tabular">{describeDays(g.median)}</td>
                <td className="py-2 text-[12.5px] text-text-muted">{g.people} people</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {missing.length ? (
        <Banner tone="warn">
          {missing.length} engine participant{missing.length > 1 ? "s are" : " is"} not on this
          master list (different email, or missing from the list).{" "}
          <button
            type="button"
            className="font-semibold underline"
            onClick={() =>
              downloadCsv(
                "gateway-not-in-master-list.csv",
                ["email", "participant_id", "track", "steps"],
                missing.map((r) => [
                  r.email,
                  r.participantId ?? "",
                  r.track ?? "",
                  r.stepAt.filter(Boolean).length,
                ]),
              )
            }
          >
            Download them
          </button>{" "}
          to check.
        </Banner>
      ) : null}
    </div>
  );
}
